// Custom-code page bridge — lets a sandboxed custom_code/custom_html iframe
// talk to the hosting page through a capability-scoped postMessage protocol.
//
// The block's JS never runs in the parent origin: it stays inside the
// `sandbox="allow-scripts"` srcdoc iframe (origin "null"). When the block
// opts in (`block.bridge.enabled`) the host injects a small `window.twasta`
// SDK (bridgeSdkScript) as the FIRST script in the srcdoc, and the renderer
// attaches useCustomCodeBridge() which:
//   - pushes an allowlist-filtered page-vars snapshot into the iframe on
//     mount and on every change (the SDK's `twasta.vars` mirror);
//   - answers request/reply ops (set_var / run_action / navigate / message /
//     get_data), each gated by the block's CustomCodeBridgeConfig allowlists.
//
// Capabilities are supplied by the surface (standalone runtime, in-app
// preview) as a CustomCodeBridgeCaps object — either through the
// CustomCodeBridgeHostContext (package renderer path) or passed directly
// (frontend's local renderer copy). No caps provider → bridge inert, the
// block renders exactly as before.
//
// Security invariants:
//   - replies/pushes go to the iframe's contentWindow only, and incoming
//     messages are accepted only when event.source === that contentWindow;
//   - targetOrigin is '*' by necessity (a sandboxed srcdoc iframe has the
//     opaque origin "null", which cannot be named) — source checking plus
//     the per-block allowlists are the actual gate;
//   - an empty/missing allowlist grants nothing; '*' grants all of a kind.

import { createContext, useContext, useEffect, useRef } from 'react'
import type React from 'react'
import type { CustomCodeBridgeConfig } from '@twasta/page-schema'

export type { CustomCodeBridgeConfig }

// ─── Capabilities a surface hands the bridge ─────────────────────────

export interface CustomCodeBridgeCaps {
  /** Live page vars (reactive — a new object per change). */
  vars: Record<string, unknown>
  setVar?: (name: string, value: unknown) => void
  runAction?: (name: string) => Promise<void>
  navigate?: (objType: string, objName: string) => void
  showMessage?: (level: 'success' | 'error' | 'info', text: string) => void
  /** Resolve a data source (by name or id) to its already-fetched rows. */
  getData?: (nameOrId: string) => unknown
}

// Surfaces that render blocks via the package's CustomHtmlRenderer provide
// caps through this context. Default null = bridge unavailable.
export const CustomCodeBridgeHostContext = createContext<CustomCodeBridgeCaps | null>(null)
export function useCustomCodeBridgeCaps(): CustomCodeBridgeCaps | null {
  return useContext(CustomCodeBridgeHostContext)
}

// ─── Allowlist helpers ───────────────────────────────────────────────

export function bridgeAllows(list: string[] | undefined, name: string): boolean {
  if (!Array.isArray(list) || list.length === 0) return false
  return list.includes('*') || list.includes(name)
}

function filterVars(vars: Record<string, unknown>, list: string[] | undefined): Record<string, unknown> {
  if (!Array.isArray(list) || list.length === 0) return {}
  if (list.includes('*')) return { ...vars }
  const out: Record<string, unknown> = {}
  for (const name of list) {
    if (name in vars) out[name] = vars[name]
    // Project vars carry a companion human label — expose it alongside.
    const label = `${name}__label`
    if (label in vars) out[label] = vars[label]
  }
  return out
}

// ─── The injected iframe SDK (window.twasta) ─────────────────────────
// Plain ES5-ish so it runs in any iframe without a build step. Kept as a
// single self-executing string so renderers can inline it into srcdoc.

export function bridgeSdkScript(): string {
  return `(function(){
  'use strict';
  var seq = 0, pending = {}, snapshot = {}, listeners = [], gotFirst = false;
  var readyResolve; var ready = new Promise(function(r){ readyResolve = r; });
  function call(op, args){
    return new Promise(function(resolve, reject){
      var id = 'twb' + (++seq);
      pending[id] = { resolve: resolve, reject: reject };
      try { parent.postMessage({ __twastaBridge: 1, kind: 'call', id: id, op: op, args: args || {} }, '*'); }
      catch (e) { delete pending[id]; reject(e); }
    });
  }
  window.addEventListener('message', function(ev){
    var d = ev && ev.data;
    if (!d || d.__twastaBridge !== 1) return;
    if (d.kind === 'vars') {
      snapshot = d.vars || {};
      if (!gotFirst) { gotFirst = true; try { readyResolve(); } catch (e) {} }
      for (var i = 0; i < listeners.length; i++) { try { listeners[i](snapshot); } catch (e) {} }
      return;
    }
    if (d.kind === 'reply') {
      var p = pending[d.id];
      if (!p) return;
      delete pending[d.id];
      if (d.ok) p.resolve(d.result);
      else p.reject(new Error(d.error || 'twasta bridge error'));
    }
  });
  window.twasta = {
    ready: ready,
    vars: {
      get: function(name){ return snapshot[name]; },
      getAll: function(){ var out = {}; for (var k in snapshot) out[k] = snapshot[k]; return out; },
      set: function(name, value){ return call('set_var', { name: name, value: value }); },
      onChange: function(cb){
        listeners.push(cb);
        if (gotFirst) { try { cb(snapshot); } catch (e) {} }
        return function(){ var i = listeners.indexOf(cb); if (i >= 0) listeners.splice(i, 1); };
      }
    },
    actions: { run: function(name){ return call('run_action', { name: name }); } },
    navigate: function(objType, objName){ return call('navigate', { objType: objType, objName: objName }); },
    message: function(level, text){ return call('message', { level: level, text: text }); },
    data: { get: function(name){ return call('get_data', { name: name }); } }
  };
})();`
}

// Static stub for the SSR surface: same window.twasta shape so author code
// doesn't crash on public sites, but vars are baked read-only at prerender
// and every mutating op rejects.
export function bridgeSdkStubScript(vars: Record<string, unknown>): string {
  let baked = '{}'
  try { baked = JSON.stringify(vars ?? {}) } catch { /* non-serializable var — ship empty */ }
  // Guard "</script>" sequences so the baked JSON can't break out of the tag.
  baked = baked.replace(/</g, '\\u003c')
  return `(function(){
  'use strict';
  var snapshot = ${baked};
  function unavailable(op){ return Promise.reject(new Error('twasta.' + op + ' is not available on a static site')); }
  window.twasta = {
    ready: Promise.resolve(),
    vars: {
      get: function(name){ return snapshot[name]; },
      getAll: function(){ var out = {}; for (var k in snapshot) out[k] = snapshot[k]; return out; },
      set: function(){ return unavailable('vars.set'); },
      onChange: function(cb){ try { cb(snapshot); } catch (e) {} return function(){}; }
    },
    actions: { run: function(){ return unavailable('actions.run'); } },
    navigate: function(){ return unavailable('navigate'); },
    message: function(){ return unavailable('message'); },
    data: { get: function(){ return unavailable('data.get'); } }
  };
})();`
}

// ─── Host-side hook ──────────────────────────────────────────────────
// Attach to the iframe that renders the block. Returns true when the bridge
// is live (SDK should be injected into srcdoc). All op handling reads the
// latest caps/config via refs so the message listener binds once.

export function useCustomCodeBridge(
  frameRef: React.RefObject<HTMLIFrameElement | null>,
  bridge: CustomCodeBridgeConfig | undefined,
  caps: CustomCodeBridgeCaps | null,
): boolean {
  const active = !!bridge?.enabled && !!caps
  const capsRef = useRef(caps); capsRef.current = caps
  const bridgeRef = useRef(bridge); bridgeRef.current = bridge

  // Serialize the allowlist-filtered snapshot so the push effect fires only
  // when a visible var actually changes (not on every parent render).
  let snapshotJson = '{}'
  if (active && caps) {
    try { snapshotJson = JSON.stringify(filterVars(caps.vars, bridge?.vars)) } catch { snapshotJson = '{}' }
  }

  // Push the vars snapshot on change AND on iframe (re)load — srcdoc resets
  // wipe the SDK's mirror, and the SDK resolves `twasta.ready` on first push.
  useEffect(() => {
    if (!active) return
    const el = frameRef.current
    if (!el) return
    const push = () => {
      try { el.contentWindow?.postMessage({ __twastaBridge: 1, kind: 'vars', vars: JSON.parse(snapshotJson) }, '*') }
      catch { /* iframe not ready yet — the load handler re-pushes */ }
    }
    push()
    el.addEventListener('load', push)
    return () => { el.removeEventListener('load', push) }
  }, [active, snapshotJson, frameRef])

  // Request/reply op handling — one listener for the block's lifetime.
  useEffect(() => {
    if (!active) return
    const onMessage = (ev: MessageEvent) => {
      const el = frameRef.current
      if (!el || ev.source !== el.contentWindow) return
      const d = ev.data as { __twastaBridge?: number; kind?: string; id?: string; op?: string; args?: Record<string, unknown> } | null
      if (!d || d.__twastaBridge !== 1 || d.kind !== 'call' || !d.id || !d.op) return
      const reply = (ok: boolean, result?: unknown, error?: string) => {
        try { el.contentWindow?.postMessage({ __twastaBridge: 1, kind: 'reply', id: d.id, ok, result, error }, '*') }
        catch { /* iframe gone */ }
      }
      const cfg = bridgeRef.current
      const cap = capsRef.current
      if (!cfg?.enabled || !cap) { reply(false, undefined, 'bridge disabled'); return }
      const args = d.args || {}
      void (async () => {
        try {
          switch (d.op) {
            case 'set_var': {
              const name = String(args.name ?? '')
              if (!bridgeAllows(cfg.vars, name)) throw new Error(`var "${name}" is not allowlisted on this block`)
              if (!cap.setVar) throw new Error('vars are read-only on this surface')
              cap.setVar(name, args.value)
              reply(true)
              return
            }
            case 'run_action': {
              const name = String(args.name ?? '')
              if (!bridgeAllows(cfg.actions, name)) throw new Error(`action "${name}" is not allowlisted on this block`)
              if (!cap.runAction) throw new Error('actions are not available on this surface')
              await cap.runAction(name)
              reply(true)
              return
            }
            case 'navigate': {
              if (!cfg.navigate) throw new Error('navigation is not allowlisted on this block')
              if (!cap.navigate) throw new Error('navigation is not available on this surface')
              cap.navigate(String(args.objType ?? 'S'), String(args.objName ?? ''))
              reply(true)
              return
            }
            case 'message': {
              const level = args.level === 'error' || args.level === 'success' ? args.level : 'info'
              if (!cap.showMessage) throw new Error('messages are not available on this surface')
              cap.showMessage(level, String(args.text ?? ''))
              reply(true)
              return
            }
            case 'get_data': {
              const name = String(args.name ?? '')
              if (!bridgeAllows(cfg.data, name)) throw new Error(`data source "${name}" is not allowlisted on this block`)
              if (!cap.getData) throw new Error('data sources are not available on this surface')
              reply(true, cap.getData(name))
              return
            }
            default:
              throw new Error(`unknown bridge op "${d.op}"`)
          }
        } catch (err) {
          reply(false, undefined, err instanceof Error ? err.message : String(err))
        }
      })()
    }
    window.addEventListener('message', onMessage)
    return () => { window.removeEventListener('message', onMessage) }
  }, [active, frameRef])

  return active
}
